import type { ConclusionType } from '~/model/ConclusionType';
import type { InteractionType } from '~/model/InteractionType';
import type { QuestionReconstructedType, QuestionType } from '~/model/QuestionType';
import type { SectionType } from '~/model/SectionType';
import { isAfter, isBefore } from 'date-fns';
import { InteractionStatus } from '@/app/types/entities/interaction';
import { statusContribution } from '~/const/api';
import appConst from '~/const/appConst';

export function isEntityEmpty(entity: SectionType | ConclusionType) {
  return (
    entity
    && (entity.title === '' || entity.title === null)
    && (entity.body === '' || entity.body === null || entity.body === '<p><br /></p>')
    && entity?.media === null
  );
};

export function checkIfInteractionIsComing(interaction: InteractionType) {
  const status = interaction?.status;
  const now = Date.now();
  return status !== undefined && isBefore(now, new Date(interaction?.start_at));
};

export function checkIfInteractionIsFinished(interaction: InteractionType) {
  const status = interaction?.status;
  const now = Date.now();
  return !!status && (status === InteractionStatus.TRASHED || isAfter(now, new Date(interaction?.end_at)));
}

export function checkIfInteractionIsAccessible(interaction: InteractionType) {
  const isComming = checkIfInteractionIsComing(interaction);
  const isFinished = checkIfInteractionIsFinished(interaction);
  const count = interaction?.current_user_contribution?.count_per_status || {};

  const accessibleStatusList = [
    statusContribution.accepted,
    statusContribution.confirmed,
    statusContribution.submitted,
  ];

  const isSelection = interaction?.['@type'] === appConst.entityTypes.selection;

  const ongoingContributions = Object.keys(count).some(
    key => accessibleStatusList.includes(key) && !!count[key],
  );

  return !(isComming && isFinished) || !(isSelection && ongoingContributions);
}

export function reorganizeSections(
  sections: SectionType[],
  questions: Array<QuestionReconstructedType | QuestionType>,
) {
  const sectionMap = new Map(sections.map(section => [section['@id'], section]));
  const [firstQuestion] = questions || [];
  const parentSection = sectionMap.get(firstQuestion?.section);

  if (parentSection) {
    const reorganizedSections = sections.flatMap((section) => {
      if (section['@id'] === parentSection['@id']) {
        return isEntityEmpty(section) ? [firstQuestion] : [section, firstQuestion];
      }

      return [section];
    });

    return reorganizedSections;
  }

  const [first, ...rest] = sections;

  return isEntityEmpty(first) ? rest : sections;
};
